import { TOURNAMENT_GAME_RESULT } from "../../constants";
import { isPlayerInRound, calculateRoundsParticipated, calculateInitialRoundsParticipated, } from "../utils/playerUtils";
export class TournamentPlayerService {
    repository;
    constructor(repository) {
        this.repository = repository;
    }
    async getPlayers(tournamentId) {
        return this.repository.getPlayers(tournamentId);
    }
    async getPlayer(tournamentId, username) {
        return this.repository.getPlayer(tournamentId, username);
    }
    async getActivePlayersInRound(tournamentId, roundNumber) {
        const players = await this.repository.getPlayers(tournamentId);
        return players.filter((player) => isPlayerInRound(player, roundNumber) && player.active);
    }
    async addPlayer(tournamentId, user) {
        const players = await this.repository.getPlayers(tournamentId);
        const existingPlayer = players.find((p) => p.username === user.username);
        if (existingPlayer) {
            await this.repository.updatePlayer(tournamentId, user.username, {
                user,
                deletedAt: null,
            });
            console.log("Updated player", user.username, "in tournament");
            return;
        }
        const newPlayer = {
            username: user.username,
            score: 0,
            rating: 0,
            roundsParticipated: 0,
            user,
            deletedAt: null,
            startingRank: players.length + 1,
            seed: players.length + 1,
            games: [],
            scoreBeforeGame: 0,
            currentRank: 0,
        };
        await this.repository.createPlayer(tournamentId, newPlayer);
        console.log("Added player", user.username, "to tournament");
    }
    /**
     * Add multiple players in a single batch operation for better performance
     */
    async addPlayers(tournamentId, users, participatingPlayerIds) {
        if (!users.length)
            return;
        // Get tournament info and existing players
        const [tournament, existingPlayers] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getPlayers(tournamentId),
        ]);
        const existingUsernames = new Set(existingPlayers.map((p) => p.username));
        const participatingIds = new Set(participatingPlayerIds || []);
        // Filter out existing players and create new player objects
        const newUsers = users.filter((user) => !existingUsernames.has(user.username));
        const newPlayers = newUsers.map((user, index) => {
            // Determine if this player should participate
            const shouldParticipate = participatingPlayerIds === null || participatingIds.has(user.username);
            // Calculate appropriate rounds participated bitmask for new player
            const roundsParticipated = calculateInitialRoundsParticipated(tournament.currentRound || 0, tournament.totalRounds, shouldParticipate);
            return {
                username: user.username,
                score: 0,
                rating: 0,
                roundsParticipated,
                user,
                deletedAt: null,
                startingRank: existingPlayers.length + index + 1,
                seed: existingPlayers.length + index + 1,
                games: [],
                scoreBeforeGame: 0,
                currentRank: 0,
            };
        });
        if (newPlayers.length > 0) {
            await this.repository.createPlayers(tournamentId, newPlayers);
            const participatingCount = newPlayers.filter((p) => p.roundsParticipated > 0).length;
            const nonParticipatingCount = newPlayers.length - participatingCount;
            console.log(`Added ${newPlayers.length} players to tournament in batch (${participatingCount} participating, ${nonParticipatingCount} non-participating)`);
        }
    }
    async removePlayer(tournamentId, username) {
        await this.repository.removePlayer(tournamentId, username);
    }
    async updatePlayer(tournamentId, username, data) {
        await this.repository.updatePlayer(tournamentId, username, data);
    }
    async participate(tournamentId, username, roundNumber = null, user = null) {
        const tournament = await this.repository.getTournament(tournamentId);
        if (!tournament)
            return;
        const currentRound = Math.max(roundNumber ?? tournament.currentRound, 1);
        const player = await this.repository.getPlayer(tournamentId, username);
        if (!player || isPlayerInRound(player, currentRound)) {
            return;
        }
        const newRoundsParticipated = calculateRoundsParticipated(tournament.currentRound, tournament.totalRounds, player.roundsParticipated, currentRound, true);
        const updateData = {
            roundsParticipated: newRoundsParticipated,
        };
        if (user) {
            updateData.user = user;
        }
        await this.repository.updatePlayer(tournamentId, player.username, updateData);
    }
    async unparticipate(tournamentId, username, roundNumber = null) {
        const tournament = await this.repository.getTournament(tournamentId);
        if (!tournament)
            return;
        const currentRound = Math.max(roundNumber ?? tournament.currentRound, 1);
        const player = await this.repository.getPlayer(tournamentId, username);
        if (!player || !isPlayerInRound(player, currentRound)) {
            return;
        }
        const newRoundsParticipated = calculateRoundsParticipated(tournament.currentRound, tournament.totalRounds, player.roundsParticipated, currentRound, false);
        await this.repository.updatePlayer(tournamentId, player.username, {
            roundsParticipated: newRoundsParticipated,
        });
    }
    async handleForfeitedPlayers(tournamentId, roundNumber) {
        // eslint-disable-next-line
        const [games, _] = await Promise.all([
            this.repository.getGames(tournamentId),
            this.repository.getPlayers(tournamentId),
        ]);
        const roundGames = games.filter((g) => g.roundNumber === roundNumber);
        const forfeitedPlayers = this.getForfeitedPlayers(roundGames);
        await Promise.all(Array.from(forfeitedPlayers).map((username) => this.unparticipate(tournamentId, username, roundNumber)));
        return forfeitedPlayers.size;
    }
    getForfeitedPlayers(games) {
        const forfeitedPlayers = new Set();
        for (const game of games) {
            if (game.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITL) {
                forfeitedPlayers.add(game.whiteUsername);
            }
            if (game.blackResult === TOURNAMENT_GAME_RESULT.FORFEITL) {
                forfeitedPlayers.add(game.blackUsername);
            }
        }
        return forfeitedPlayers;
    }
}
